import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import PublicShell from '../components/PublicShell'
import Spinner from '../components/Spinner'
import { referralsAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { savePendingReferral } from '../utils/referral'

/**
 * /join/<code>: a subscriber's share link. The code is remembered in this
 * browser (utils/referral), so subscribing to that creator, now or after
 * signing up, counts it. Signed in, it goes straight to the creator.
 */
const JoinReferral = () => {
  const { code = '' } = useParams()
  const token = useAuthStore((state) => state.token)
  const [creator, setCreator] = useState<{ id: number; name: string } | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    referralsAPI
      .track(code)
      .then((link) => {
        if (!live) return
        savePendingReferral(link.referral_code, link.creator_id)
        setCreator({ id: link.creator_id, name: link.creator_display_name })
      })
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [code])

  if (creator && token) return <Navigate to={`/creator/${creator.id}`} replace />

  return (
    <PublicShell>
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        {failed ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900">This invite link doesn&apos;t work</h1>
            <p className="mt-2 text-gray-600">Ask whoever sent it for a new one, or look around FinCreator.</p>
            <Link to="/" className="mt-6 inline-block text-primary-600 hover:underline">
              Go to FinCreator
            </Link>
          </>
        ) : !creator ? (
          <Spinner />
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900">You&apos;re invited to follow {creator.name}</h1>
            <p className="mt-2 text-gray-600">
              Sign up or log in, then subscribe from {creator.name}&apos;s page. The friend who sent this link is
              thanked when you do.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link
                to="/register"
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                Sign up
              </Link>
              <Link to="/login" className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Log in
              </Link>
            </div>
          </>
        )}
      </div>
    </PublicShell>
  )
}

export default JoinReferral
